import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { User } from './models/user';
import { CustomizationService } from './services/customization.service';
import { ErrorService } from './services/error.service';
import { GlobalService } from './services/global.service';
import { UserService } from './services/user.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable()
export class AppConfig implements OnDestroy {
	sub$ = new Subscription();

	constructor(private _swUpdate: SwUpdate,
				private _httpClient: HttpClient,
				private _customizationService: CustomizationService,
				private _userService: UserService,
				private _globalService: GlobalService,
				private _errorService: ErrorService) {
	}

	getAnonymousOptions() {
		let user = this._userService.getUser();
		this.sub$.add(this._globalService.getAnonymousGlobalOptions().subscribe({
			next: (globalSettings) => {
				if (user) {
					if (user.globalSettings) {
						Object.assign(user.globalSettings, user.globalSettings, globalSettings);
					} else {
						user.globalSettings = globalSettings;
					}
				} else {
					user = new User();
					user.globalSettings = globalSettings;
				}
				this._userService.updateUser(user);
			}, error: (error) => {
				this._errorService.error(error, 'getAnonymousGlobalOptions failed');
			}
		}));
	}

	getCustomSettings() {
		return this._httpClient.get<any>('./app/config/custom-settings.json');
	}

	public load() {
		return new Promise((resolve) => {
			this.checkServiceWorkerRegistration();
			if (this._swUpdate.isEnabled) {
				this.sub$.add(this._swUpdate.versionUpdates.pipe(
          filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
        ).subscribe(() => {
					this._swUpdate.unrecoverable.subscribe(event => {
						alert(
							`An error occurred that we cannot recover from:\n${event?.reason}\n\n` +
							'Please reload the page.');
					});
					alert('Client Portal has been updated to the latest version to ensure optimal functionality, stability, and user experience. If you had any open orders, please check to confirm your data has properly saved.');
					this._swUpdate.activateUpdate().then(() => document.location.reload());
				}));
			}
			// This is the default webservice api url, we will read in the customSettings.json and if the url is different
			// we will update it to the one in the customSettings.json and save to localStorage otherwise save default
			this.sub$.add(this.getCustomSettings().subscribe({
				next: (customSettings) => {
					if (customSettings.googleapis) {
						const script: any = document.getElementById('googleapis');
						if (document.getElementById('googleapis')) {
							script.src = customSettings.googleapis;
						}
					}
					if (customSettings.favicon) {
						if (document.getElementById('favicon')) {
							const link: any = document.getElementById('favicon');
							// setTimeout(()=>{
							link.href = customSettings.favicon;
							// }, 1000)
						}
					}
					if (customSettings.apiUrl && customSettings.apiUrl !== '/CxtWebService/CXTWCF.svc') {
						localStorage.setItem('Rapidship:apiUrl', customSettings.apiUrl);
						environment.apiUrl = customSettings.apiUrl;
					} else {
						localStorage.setItem('Rapidship:apiUrl', '/CxtWebService/CXTWCF.svc');
						environment.apiUrl = '/CxtWebService/CXTWCF.svc';
					}
					if (customSettings.hasOwnProperty('siteKey')) {
						environment.siteKey = customSettings.siteKey;
					}
					if (customSettings.hasOwnProperty('siteId')) {
						environment.siteId = customSettings.siteId;
					}
					this._customizationService.getStyleVariables(environment.siteId).subscribe({
						next: (styleVariables) => {
							const styleSheet = document.createElement('style');
							styleSheet.innerText = styleVariables;
							document.head.appendChild(styleSheet)
						}, error: (error) => {
							this._errorService.error(error, 'getStyleVariables failed', 'modal');
						}
					});
					this._customizationService.getCustomWidgets(environment.siteId).subscribe({
						next: (widgetConfig: any) => {
							if (widgetConfig) {
								// Save customer and page options
								widgetConfig = this._customizationService.saveWidgetConfig(widgetConfig);

								// Load script
								if (widgetConfig.loadScript) {
									const widgetScript = document.createElement('script');
									widgetScript.async = true;

									if (widgetConfig.scriptType === 'URL_REFERENCE') {
										widgetScript.src = widgetConfig.loadScript;
										widgetScript.id = 'ze-snippet'; // This name is required for ZenDesk widget to work.
									} else if (widgetConfig.scriptType === 'SCRIPT_BLOCK') {
										widgetScript.textContent = widgetConfig.loadScript;
									}

									document.body.appendChild(widgetScript);

									// When script is done loading, signal all the other components that it is ready.
									// For URL_REFERENCE scripts, onload fires when the external script finishes loading.
									// For SCRIPT_BLOCK (inline) scripts, onload never fires on inline elements,
									// so signal immediately after append since the script runs synchronously.
									if (widgetConfig.scriptType === 'SCRIPT_BLOCK') {
										this._customizationService.widgetScriptLoaded$.next(true);
									} else {
										widgetScript.onload = () => {
											this._customizationService.widgetScriptLoaded$.next(true);
										};
										widgetScript.onerror = (err) => {
											console.error('Widget script failed to load:', err);
										};
									}
								}
							}
						}, error: (error) => {
							this._errorService.error(error, 'getCustomWidgets failed', 'modal');
						}
					});

					this.getAnonymousOptions();
					resolve(true);
				}, error: (error) => {
					this._errorService.error(error, 'Get custom-settings failed', 'modal');
					localStorage.setItem('Rapidship:apiUrl', '/CxtWebService/CXTWCF.svc');
					environment.apiUrl = '/CxtWebService/CXTWCF.svc';
					this.getAnonymousOptions();
					resolve(true);
				}
			}));
		});
	}

	private checkServiceWorkerRegistration(): void {
		if ('serviceWorker' in navigator) {
			// CRITICAL: Run transition FIRST without waiting for service worker ready
			// If we wait for serviceWorker.ready, broken/old service workers will never resolve
			// and the transition code that fixes them will never run (deadlock)
			this.oneTimeSwTransition();
		}
	}

	/**
	 * One-time transition helper to clear old caches for users upgrading to 25.8.0
	 *
	 * TODO: REMOVE THIS METHOD IN VERSION 25.9.0 OR LATER
	 *
	 * Context: We deploy the same artifact to all environments, so existing users
	 * with cached ngsw.json won't benefit from the new Cache-Control headers until
	 * the NEXT release. This helper ensures all users get the fix immediately.
	 *
	 * See: docs/service-worker-fix-plan.md for full details
	 */
	private async oneTimeSwTransition(): Promise<void> {
		try {
			const transitionDone = localStorage.getItem('Rapidship:swTransition_v1');
			if (!transitionDone) {
				console.log('🔄 Running one-time service worker transition...');

				// Unregister all service workers
				const registrations = await navigator.serviceWorker.getRegistrations();
				for (const registration of registrations) {
					await registration.unregister();
				}

				// Clear all caches
				if ('caches' in window) {
					const cacheNames = await caches.keys();
					await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
				}

				// Mark transition as complete
				localStorage.setItem('Rapidship:swTransition_v1', '1');

				// Reload to get fresh content
				window.location.reload();
			}
		} catch (error) {
			console.error('❌ Error during service worker transition:', error);
		}
	}

  ngOnDestroy(): void {
    this.sub$.unsubscribe();
  }
}
